import * as THREE from "three";
import { MATS } from "../materials.js";
import * as H from "./spec.js";

/**
 * Glazing and doors for the hero house.
 *
 * Every rectangle in H.OPENINGS is filled here; the shell leaves each one as
 * a recess ~0.10 m deep behind its `plane`.  Everything sits in layers inside
 * that recess so nothing is ever coplanar with anything else:
 *
 *      glass        -0.072 .. -0.054   (dark, behind everything)
 *      muntins      -0.052 .. -0.024   (verticals and horizontals on two
 *                                       sub-layers so crossings never share
 *                                       a face)
 *      frame        -0.055 .. -0.005   (rails and stiles on two sub-layers,
 *                                       so butt joints never share a face)
 *      casing       -0.020 .. +0.044   (proud of the brick)
 *
 * Only sill boards, the doorstep, and the closed door slabs carry colliders --
 * a cat bumping invisible window frames feels broken, but a wide sill is a
 * perch and a shut door is a wall.
 *
 * Two doors are NOT shut, because the house has an inside now: the front door
 * stands 92 degrees open into the hall, and the back wall's patio slider has
 * one leaf parked behind the other.  Both are drawn where they actually are,
 * so the collider follows the leaf rather than the opening.
 */

const TRIM  = MATS[H.LOOK.trim];
const SHADE = MATS[H.LOOK.trimShade];
const GLASS = MATS[H.LOOK.glass];
const DOOR  = MATS[H.LOOK.door];
const GDOOR = MATS[H.LOOK.garageDoor];
const STONE = MATS[H.LOOK.stone];
const MDARK = MATS.metalDark;
const MPALE = MATS.metalPale;

/** Depth layers, as {offset of centre, thickness} along a pane's normal. */
const L = {
  glass: { off: -0.063, d: 0.018 },
  rail:  { off: -0.030, d: 0.050 },   // horizontal frame members
  stile: { off: -0.030, d: 0.046 },   // vertical frame members
  meet:  { off: -0.028, d: 0.040 },   // sash meeting rail
  mv:    { off: -0.040, d: 0.024 },   // vertical muntin bars
  mh:    { off: -0.036, d: 0.024 },   // horizontal muntin bars
  case:  { off: +0.010, d: 0.060 },   // side casing boards
  head:  { off: +0.014, d: 0.060 },   // head casing board
};

const BURY = 0.012;   // how far members sink into each other at joints, so
                      // butt ends are hidden inside the other piece instead
                      // of leaving a coplanar seam

/**
 * One box in a glazing pane's local frame: `u` runs along the pane, `off`
 * along its outward normal, `ry` is the pane's rotation about Y.  This is
 * what lets the bay's angled cheeks share every helper with flat windows.
 */
function paneBox(b, mat, px, pz, ry, u, layer, y, w, h, opts = {}) {
  const g = new THREE.BoxGeometry(w, h, layer.d);
  g.translate(u, y + h / 2, layer.off);
  if (ry) g.rotateY(ry);
  g.translate(px, 0, pz);
  b.add(g, mat, { collider: null, ...opts });
}

/**
 * A complete glazed unit: perimeter frame, glass, and a cols x rows muntin
 * grid.  With topRail:false the caller provides its own head member (the
 * Palladian transom) and `h` is measured to just inside it.
 */
function glazedPane(b, opts) {
  const { px, pz, ry = 0, w, h, y0, cols = 1, rows = 1, fw = 0.06,
          topRail = true } = opts;
  const yb = y0 + fw;
  const yt = topRail ? y0 + h - fw : y0 + h;
  const bt = topRail ? BURY : 0;
  const iw = w - 2 * fw;
  const ih = yt - yb;

  paneBox(b, TRIM, px, pz, ry, 0, L.rail, y0, w, fw);
  if (topRail) paneBox(b, TRIM, px, pz, ry, 0, L.rail, y0 + h - fw, w, fw);
  for (const s of [-1, 1])
    paneBox(b, TRIM, px, pz, ry, s * (w - fw) / 2, L.stile,
            yb - BURY, fw, ih + BURY + bt);
  paneBox(b, GLASS, px, pz, ry, 0, L.glass,
          yb - BURY, iw + 2 * BURY, ih + BURY + bt);
  for (let i = 1; i < cols; i++)
    paneBox(b, TRIM, px, pz, ry, -iw / 2 + (iw * i) / cols, L.mv,
            yb - BURY, 0.024, ih + BURY + bt);
  for (let j = 1; j < rows; j++)
    paneBox(b, TRIM, px, pz, ry, 0, L.mh,
            yb + (ih * j) / rows - 0.012, iw + 2 * BURY, 0.024);
}

/**
 * Cream surround for a FACE-plane opening: side casings, head board, and a
 * projecting sill.  The sill is the one piece here with a collider -- a wide
 * sill is a classic cat perch, and the wall below always supports it.
 */
function surround(b, o, opts = {}) {
  const { sillExt = 0.10, sillD = 0.18 } = opts;
  const yb = o.y - o.h / 2, yt = o.y + o.h / 2;
  for (const s of [-1, 1])
    paneBox(b, TRIM, o.x, o.plane, 0, s * (o.w / 2 + 0.02), L.case,
            yb + 0.005, 0.10, o.h - 0.005);
  // head width stays at +0.16 so the paired windows' heads clear each other
  paneBox(b, TRIM, o.x, o.plane, 0, 0, L.head, yt - 0.02, o.w + 0.16, 0.10);
  b.block(o.x, yb - 0.05, o.plane - 0.02, o.w + 2 * sillExt, 0.07, sillD, TRIM);
}

/** An ordinary sash: 2x2 muntin grid in the upper sash, plain lower sash. */
function sashWindow(b, o) {
  glazedPane(b, { px: o.x, pz: o.plane, w: o.w, h: o.h, y0: o.y - o.h / 2 });
  const iw = o.w - 0.12;
  paneBox(b, TRIM, o.x, o.plane, 0, 0, L.meet, o.y - 0.025, iw + 2 * BURY, 0.05);
  const gb = o.y + 0.025, gt = o.y + o.h / 2 - 0.06;   // upper-sash glass
  paneBox(b, TRIM, o.x, o.plane, 0, 0, L.mv, gb - BURY, 0.024, gt - gb + 2 * BURY);
  paneBox(b, TRIM, o.x, o.plane, 0, 0, L.mh,
          (gb + gt) / 2 - 0.012, iw + 2 * BURY, 0.024);
  surround(b, o);
}

// ---------------------------------------------------------------------------
// The Palladian window over the porch
// ---------------------------------------------------------------------------
function palladian(b, o) {
  const p = o.plane;
  const yb = o.y - o.h / 2, yt = o.y + o.h / 2;
  const spring = yt - o.arch;              // where the fanlight takes over
  const a = o.w / 2;

  // Lower light: 4x3 grid, topped by the transom instead of its own rail.
  glazedPane(b, { px: o.x, pz: p, w: o.w, h: spring + BURY - yb, y0: yb,
                  cols: 4, rows: 3, topRail: false });
  paneBox(b, TRIM, o.x, p, 0, 0, L.rail, spring - 0.035, o.w, 0.07);

  // Cream tympanum panel at the back of the recess: it fills the square
  // corners the shell leaves around the curve, and backs the dark glass.
  b.block(o.x, spring - 0.02, p - 0.085, o.w - 0.02, o.arch + 0.015, 0.014,
          TRIM, { solid: false });

  // Fanlight: rise 0.62 over a 2.55 span reads as a half-ellipse, so every
  // curve here is a unit semicircle scaled to (a, rise).
  const gl = new THREE.CircleGeometry(1, 24, 0, Math.PI);
  gl.scale(a - 0.03, o.arch - 0.03, 1);
  gl.translate(o.x, spring, p - 0.054);
  b.add(gl, GLASS, { collider: null });

  // Radiating spokes, each aimed at its own point on the ellipse (a straight
  // fan of equal angles would miss a squashed curve).
  const r0 = 0.10, sa = a - 0.055, sb = o.arch - 0.055;
  for (const deg of [30, 60, 90, 120, 150]) {
    const t = (deg * Math.PI) / 180;
    const ex = sa * Math.cos(t), ey = sb * Math.sin(t);
    const len = Math.hypot(ex, ey) - r0 + 0.05;
    const g = new THREE.BoxGeometry(len, 0.026, L.mv.d);
    g.translate(r0 - 0.01 + len / 2, 0, 0);
    g.rotateZ(Math.atan2(ey, ex));
    g.translate(o.x, spring, p + L.mv.off);
    b.add(g, TRIM, { collider: null });
  }
  const hub = new THREE.CircleGeometry(0.13, 12, 0, Math.PI);
  hub.translate(o.x, spring, p - 0.026);
  b.add(hub, TRIM, { collider: null });

  // slim inner ring where the spokes end -- the "spider web" a real fanlight
  // has.  It sits on the horizontal-muntin layer so crossings with the
  // spokes (vertical-muntin layer) never share a face.
  const ra = a - 0.30, rb = o.arch - 0.22;
  let rx = ra, ry2 = 0;
  for (let i = 1; i <= 12; i++) {
    const t = (Math.PI * i) / 12;
    const qx = ra * Math.cos(t), qy = rb * Math.sin(t);
    const g = new THREE.BoxGeometry(Math.hypot(qx - rx, qy - ry2) + 0.02,
                                    0.026, L.mh.d);
    g.rotateZ(Math.atan2(qy - ry2, qx - rx));
    g.translate(o.x + (rx + qx) / 2, spring + (ry2 + qy) / 2, p + L.mh.off);
    b.add(g, TRIM, { collider: null });
    rx = qx; ry2 = qy;
  }

  // Heavy surround: an arch band of short boxes along the ellipse (uniform
  // thickness, unlike a scaled torus), side casings, keystone, sill.
  const ba = a + 0.01, bb = o.arch + 0.01, N = 12;
  let px = ba, py = 0;
  for (let i = 1; i <= N; i++) {
    const t = (Math.PI * i) / N;
    const qx = ba * Math.cos(t), qy = bb * Math.sin(t);
    const g = new THREE.BoxGeometry(Math.hypot(qx - px, qy - py) + 0.025,
                                    0.09, L.case.d);
    g.rotateZ(Math.atan2(qy - py, qx - px));
    g.translate(o.x + (px + qx) / 2, spring + (py + qy) / 2, p + L.case.off);
    b.add(g, TRIM, { collider: null });
    px = qx; py = qy;
  }
  for (const s of [-1, 1])
    paneBox(b, TRIM, o.x, p, 0, s * (a + 0.02), L.case,
            yb + 0.005, 0.10, spring - yb + 0.02);
  b.block(o.x, yt - 0.10, p + 0.02, 0.13, 0.24, 0.07, STONE, { solid: false });
  b.block(o.x, yb - 0.05, p - 0.02, o.w + 0.20, 0.07, 0.18, TRIM);   // sill
}

// ---------------------------------------------------------------------------
// The bay window over the garage -- the detail the client called out
// ---------------------------------------------------------------------------
function bayWindow(b) {
  const o = H.opening("bay");
  const B = H.BAY, p = o.plane;
  const bx = B.centreX;
  const front = p + B.project;             // the wide pane's plane
  const fh = B.width / 2 - B.cheek;        // front pane half-width: the cheeks
                                           // eat `cheek` of plan width each
                                           // side, leaving a 1.9 m front --
                                           // twice the cheek, as photographed
  const y0 = B.sill, h = B.head - B.sill;

  // Wide front pane, 3x3.  Chunkier 0.07 members: this frame carries the look.
  glazedPane(b, { px: bx, pz: front, w: fh * 2, h, y0, cols: 3, rows: 3,
                  fw: 0.07 });

  // Angled cheeks return all the way from the front corners to the brick:
  // pane length is the hypotenuse of (cheek, project) in plan.
  const len = Math.hypot(B.cheek, B.project);
  const ry = Math.atan2(B.project, B.cheek);
  for (const s of [-1, 1]) {
    glazedPane(b, { px: bx + s * (fh + B.cheek / 2), pz: (p + front) / 2,
                    ry: s * ry, w: len, h, y0, cols: 2, rows: 3 });
    // chunky posts covering the mitred joints at each corner
    b.block(bx + s * fh, y0, front - 0.02, 0.09, h, 0.10, TRIM, { solid: false });
    b.block(bx + s * (B.width / 2 - 0.03), y0, p + 0.06, 0.08, h, 0.10,
            TRIM, { solid: false });
  }

  // Projecting sill board: one trapezoid following the bay's plan, its back
  // edge buried 0.02 into the brick so no face lands on the wall plane.
  const sh = new THREE.Shape();
  sh.moveTo(-(B.width / 2 + 0.12), p - 0.02);
  sh.lineTo(-(fh + 0.05), front + 0.10);
  sh.lineTo(fh + 0.05, front + 0.10);
  sh.lineTo(B.width / 2 + 0.12, p - 0.02);
  sh.closePath();
  const sg = new THREE.ExtrudeGeometry(sh, { depth: 0.07, bevelEnabled: false });
  sg.rotateX(Math.PI / 2);                 // shape-Y becomes world Z, the
  sg.translate(bx, y0 + 0.02, 0);          // 0.07 extrusion hangs downward
  b.add(sg, TRIM, { collider: null });

  // The perch.  Axis-aligned boxes stepped under the angled edges so the cat
  // never stands past the visible board: one wide box under the front run,
  // then a shorter step under each cheek.
  b.collider(bx, y0 - 0.05, (p - 0.02 + front + 0.10) / 2,
             2 * fh, 0.07, front + 0.12 - p);
  for (const s of [-1, 1]) {
    b.collider(bx + s * (fh + 0.20), y0 - 0.05, p + 0.205, 0.30, 0.07, 0.45);
    b.collider(bx + s * (fh + 0.475), y0 - 0.05, p + 0.025, 0.25, 0.07, 0.09);
  }
  b.mark("heroBaySill", bx, y0 + 0.02, front);
}

// ---------------------------------------------------------------------------
// The red front door, standing open
// ---------------------------------------------------------------------------

/** How far the leaf is swung, in radians: a shade past square to the wall. */
const SWING = 1.60;

/**
 * A box in the leaf's own frame, placed as the swung door.
 *
 * `u` runs across the leaf from the hinge stile, `t` through its thickness
 * (+t is the face that pointed outdoors when the door was shut).  Every piece
 * of the door is still described by the numbers that described it closed --
 * the rotation happens once, here.
 */
function leafBox(b, hinge, mat, u, y, w, h, t, tOff = 0) {
  const g = new THREE.BoxGeometry(w, h, t);
  g.translate(u, y + h / 2, tOff);
  g.rotateY(hinge.swing);
  g.translate(hinge.x, 0, hinge.z);
  b.add(g, mat, { collider: null });
}

function frontDoor(b) {
  const o = H.opening("frontDoor");
  const p = o.plane;
  const yb = o.y - o.h / 2, yt = o.y + o.h / 2;

  // cream casing and jamb (no bottom rail on a door -- the threshold is it)
  for (const s of [-1, 1]) {
    paneBox(b, TRIM, o.x, p, 0, s * (o.w / 2 + 0.02), L.case,
            yb + 0.005, 0.10, o.h - 0.005);
    paneBox(b, TRIM, o.x, p, 0, s * (o.w / 2 - 0.03), L.stile,
            yb, 0.06, o.h - 0.048);
  }
  paneBox(b, TRIM, o.x, p, 0, 0, L.head, yt - 0.02, o.w + 0.16, 0.10);
  paneBox(b, TRIM, o.x, p, 0, 0, L.rail, yt - 0.06, o.w, 0.06);

  // The red leaf, hung on the -X jamb and swung inward into the hall.  Local
  // u is measured from that hinge, so `lw / 2` is the leaf's centreline and
  // every offset below is the old x-offset carried across unchanged.
  const lw = o.w - 0.12 + 2 * BURY;
  const mid = lw / 2;
  const hinge = { x: o.x - mid, z: p - 0.055, swing: SWING };
  const leaf = (mat, u, y, w, h, t, tOff) =>
    leafBox(b, hinge, mat, u, y, w, h, t, tOff);

  leaf(DOOR, mid, yb + 0.005, lw, o.h - 0.053, 0.06, 0);

  // Glass insert with its decorative grid over the leaf's upper two thirds.
  // Bars sit on two sub-layers (verticals then horizontals) like the muntins.
  const gy0 = 1.30, gy1 = 2.70, gw = 0.60;
  leaf(GLASS, mid, gy0, gw, gy1 - gy0, 0.009, 0.0355);
  for (const u of [-0.28, 0, 0.28])       // border stiles + centre bar
    leaf(TRIM, mid + u, gy0 + 0.028, u ? 0.04 : 0.018, gy1 - gy0 - 0.056,
         0.012, 0.047);
  for (const [y, hh] of [[gy0, 0.04], [gy1 - 0.04, 0.04],
                         [1.755, 0.018], [2.21, 0.018]])
    leaf(TRIM, mid, y, gw, hh, 0.012, 0.051);

  // kick plate and handle
  leaf(MPALE, mid, 0.62, 0.84, 0.24, 0.008, 0.035);
  leaf(MDARK, mid + 0.375, 1.45, 0.03, 0.18, 0.012, 0.037);
  leaf(MDARK, mid + 0.31, 1.553, 0.13, 0.024, 0.024, 0.049);
  leaf(MDARK, mid + 0.31, 1.553, 0.13, 0.024, 0.024, -0.049);   // inside lever

  // doorstep threshold: 4 cm proud of the porch deck, an easy step for a cat
  b.block(o.x, 0.50, p + 0.16, 1.22, 0.09, 0.30, STONE);
  b.mark("heroDoorstep", o.x, 0.59, p + 0.16);

  // The open leaf is still a wall, just not across the doorway: it stands
  // along the hall's -X side, from the hinge back to (-0.51, 0.49).  The
  // clear way in is everything to its +X, about 0.95 m of it.
  const tipX = hinge.x + lw * Math.cos(SWING);
  const tipZ = hinge.z - lw * Math.sin(SWING);
  b.collider((hinge.x + tipX) / 2, yb, (hinge.z + tipZ) / 2,
             Math.abs(tipX - hinge.x) + 0.10, o.h, Math.abs(tipZ - hinge.z));
  b.mark("heroFrontDoor", o.x + 0.20, H.INSIDE.floor, p - 0.30);

  porchLantern(b, o.x + 0.86, p);
}

// ---------------------------------------------------------------------------
// The patio slider in the back wall, onto the deck
// ---------------------------------------------------------------------------

/**
 * Two leaves in the back wall, the -X one slid open behind the fixed one.
 *
 * A slider rather than a hinged door for a reason: the inside of this wall is
 * the head of the flight down to the ground floor (interior.js), and a leaf
 * swinging in would sweep straight across the landing.  Sliding leaves it
 * clear, and it is what a door onto a raised deck usually is anyway.
 *
 * Its sill is the deck top, which the block's slope puts level with the
 * kitchen floor: one threshold board, no step either side of it.
 */
function backDoor(b) {
  const D = H.BACK_DOOR;
  const p = D.plane;                       // z = BACK, the outer face
  const x0 = D.x - D.w / 2, x1 = D.x + D.w / 2;      // 2.30 .. 4.10
  const h = D.head - D.sill;
  const flat = { solid: false, ao: 0.03, tint: 0.04 };

  // Casing round the reveal, outside, under the stone lintel the shell set.
  for (const sg of [-1, 1])
    b.block(D.x + sg * (D.w / 2 + 0.05), D.sill - 0.04, p - 0.02, 0.10,
            h + 0.06, 0.06, TRIM, flat);
  b.block(D.x, D.head - 0.02, p - 0.02, D.w + 0.20, 0.10, 0.06, TRIM, flat);

  // Threshold: a 2 cm sill across the reveal, level between the deck boards
  // and the landing inside.  Visual only -- nothing to trip a cat crossing.
  b.block(D.x, D.sill - 0.02, p + H.WALL_T / 2, D.w + 0.10, 0.04, H.WALL_T,
          STONE, flat);

  /** One glazed leaf: a frame of 0.06 boards with the pane lapped inside it. */
  const leaf = (cx, w, z) => {
    b.block(cx, D.sill, z, w, 0.06, 0.05, TRIM, flat);
    b.block(cx, D.head - 0.06, z, w, 0.06, 0.05, TRIM, flat);
    for (const sg of [-1, 1])
      b.block(cx + sg * (w - 0.06) / 2, D.sill + 0.05, z, 0.06, h - 0.10,
              0.046, TRIM, flat);
    b.block(cx, D.sill + 0.055, z + 0.004, w - 0.11, h - 0.11, 0.014, GLASS,
            { solid: false, ao: 0, tint: 0.03 });
  };
  // The fixed leaf sits in the outer track; the open one is parked just
  // behind it, 8 cm to -X, which is what leaves the way through.
  leaf(D.openTo + 0.45, 0.90, p + 0.07);
  leaf(D.openTo + 0.37, 0.90, p + 0.17);
  b.block(D.openTo - 0.06, D.sill + 0.45, p + 0.17, 0.035, 0.26, 0.05, MDARK,
          { solid: false });                       // pull handle
  // The shut half read from the kitchen: same trick interior.js uses on the
  // windows, because this glass is the same opaque dark pane and a black
  // rectangle beside the open half is the one thing that gives it away.
  b.block(D.openTo + 0.41, D.sill + 0.06, p + 0.21, 0.98, h - 0.12, 0.02,
          MATS.glassLit, { solid: false, ao: 0, tint: 0 });

  // Head track and a cill board inside, closing the reveal top and bottom.
  b.block(D.x, D.head - 0.05, p + 0.13, D.w - 0.02, 0.05, 0.16, TRIM, flat);

  // One collider for the shut half; the shell's runs seal everything beyond
  // x0/x1 and below the sill, so the gap left is exactly the open leaf's.
  b.collider((D.openTo - 0.08 + x1) / 2, D.sill, p + 0.12,
             x1 - D.openTo + 0.08, h, 0.24);
  b.mark("heroBackDoor", (x0 + D.openTo) / 2, D.sill, p + H.WALL_T / 2);
}

/** Coach-style lantern on the brick beside the door. All display, no collider. */
function porchLantern(b, x, p) {
  b.block(x, 1.80, p + 0.017, 0.10, 0.20, 0.03, MDARK, { solid: false });
  b.block(x, 1.82, p + 0.075, 0.11, 0.025, 0.11, MDARK, { solid: false });
  b.block(x, 1.845, p + 0.075, 0.075, 0.14, 0.075, GLASS, { solid: false });
  b.block(x, 1.985, p + 0.075, 0.12, 0.02, 0.12, MDARK, { solid: false });
  b.cone(x, 2.005, p + 0.075, 0.085, 0.075, MDARK, { segments: 4 });
  b.sphere(x, 2.08, p + 0.075, 0.016, MDARK, { segments: 6, rings: 4 });
}

// ---------------------------------------------------------------------------
// The red sectional garage door
// ---------------------------------------------------------------------------

/** One small arched light: cream plate behind, dark glass in front, each a
 *  rectangle plus a scaled semicircle head. */
function archedLight(b, cx, y0, w, hRect, rise, p) {
  b.block(cx, y0 - 0.02, p - 0.051, w, hRect + 0.02, 0.014, TRIM, { solid: false });
  const c = new THREE.CircleGeometry(w / 2, 12, 0, Math.PI);
  c.scale(1, rise / (w / 2), 1);
  c.translate(cx, y0 + hRect, p - 0.0435);
  b.add(c, TRIM, { collider: null });
  b.block(cx, y0, p - 0.0375, w - 0.06, hRect, 0.009, GLASS, { solid: false });
  const g = new THREE.CircleGeometry((w - 0.06) / 2, 12, 0, Math.PI);
  g.scale(1, (rise - 0.02) / ((w - 0.06) / 2), 1);
  g.translate(cx, y0 + hRect, p - 0.0325);
  b.add(g, GLASS, { collider: null });
}

function garageDoor(b) {
  const o = H.opening("garageDoor");
  const p = o.plane, cx = o.x;
  const yb = o.y - o.h / 2, yt = o.y + o.h / 2;

  // cream surround
  for (const s of [-1, 1])
    paneBox(b, TRIM, cx, p, 0, s * (o.w / 2 + 0.02), L.case,
            yb + 0.005, 0.10, o.h - 0.025);
  paneBox(b, TRIM, cx, p, 0, 0, L.head, yt - 0.02, o.w + 0.24, 0.11);

  // The leaf is GDOOR, not DOOR: the garage door is white, and only the
  // front door stays red.
  // recessed back slab -- the visible floor of every recessed panel
  b.block(cx, yb + 0.01, p - 0.075, o.w - 0.06, o.h - 0.02, 0.03, GDOOR,
          { solid: false });

  // Four courses.  Rails span the full leaf on one depth; stiles run between
  // them a shade shallower, so the lattice never shares a face with itself.
  const hx = o.w / 2 - 0.05;               // leaf half-width
  const y0 = yb + 0.03, y1 = yt - 0.03;
  const course = (y1 - y0) / 4;
  b.block(cx, y0, p - 0.0455, hx * 2, 0.07, 0.035, GDOOR, { solid: false });
  b.block(cx, y1 - 0.07, p - 0.0455, hx * 2, 0.07, 0.035, GDOOR, { solid: false });
  for (let k = 1; k < 4; k++)
    b.block(cx, y0 + k * course - 0.035, p - 0.0455, hx * 2, 0.07, 0.035,
            GDOOR, { solid: false });
  const stileX = [-(hx - 0.035), -0.80, 0, 0.80, hx - 0.035];
  for (let k = 0; k < 4; k++) {
    const cb = (k === 0 ? y0 + 0.07 : y0 + k * course + 0.035) - 0.015;
    const ct = (k === 3 ? y1 - 0.07 : y0 + (k + 1) * course - 0.035) + 0.015;
    for (const u of stileX)
      b.block(cx + u, cb, p - 0.0455, 0.07, ct - cb, 0.030, GDOOR, { solid: false });
  }

  // The giveaway: a row of four arched lights across the top course, one
  // centred in each panel slot.
  for (const u of [-1.1825, -0.40, 0.40, 1.1825])
    archedLight(b, cx + u, 1.86, 0.56, 0.23, 0.135, p);

  // one flush collider: the closed door is a wall to the cat
  b.collider(cx, yb, p - 0.06, o.w, o.h, 0.10);

  housePlaque(b, cx, p);
}

/** The house number, above the garage door where the client will look for it. */
function housePlaque(b, cx, p) {
  // Four digits at a 0.118 pitch: 3 x 0.118 + one digit width = 0.454 of
  // numerals, leaving even margins inside a 0.60 plaque.
  b.block(cx, 2.62, p + 0.018, 0.60, 0.30, 0.03, SHADE, { solid: false });
  const ONE = "BC", THREE = "ABGCD";
  [ONE, THREE, ONE, THREE].forEach((segs, i) => {
    // A "1" is only the two right-hand bars, so it sits off-centre in its
    // cell and crowds the next digit unless it is nudged back.
    const centre = segs === ONE ? -0.039 : 0;
    segDigit(b, cx + (i - 1.5) * 0.118 + centre, 2.77, segs, p);
  });
}

/** Seven-segment style digit from little boxes; horizontal and vertical
 *  segments live 3 mm apart in depth so their corners never share a face. */
function segDigit(b, cx, cy, segs, p) {
  const w = 0.10, h = 0.17, t = 0.022, hw = w / 2, hh = h / 2;
  const HZ = p + 0.0435, VZ = p + 0.0405;
  const put = (x, y, ww, hh2, z) =>
    b.block(x, y, z, ww, hh2, 0.009, MDARK, { solid: false });
  if (segs.includes("A")) put(cx, cy + hh - t, w, t, HZ);
  if (segs.includes("G")) put(cx, cy - t / 2, w, t, HZ);
  if (segs.includes("D")) put(cx, cy - hh, w, t, HZ);
  if (segs.includes("B")) put(cx + hw - t / 2, cy, t, hh, VZ);
  if (segs.includes("C")) put(cx + hw - t / 2, cy - hh, t, hh, VZ);
  if (segs.includes("E")) put(cx - hw + t / 2, cy - hh, t, hh, VZ);
  if (segs.includes("F")) put(cx - hw + t / 2, cy, t, hh, VZ);
}

// ---------------------------------------------------------------------------
export function build(b) {
  const fill = {
    arch:       () => palladian(b, H.opening("arch")),
    upperPairL: () => sashWindow(b, H.opening("upperPairL")),
    upperPairR: () => sashWindow(b, H.opening("upperPairR")),
    bay:        () => bayWindow(b),
    livingRoom: () => {
      const o = H.opening("livingRoom");
      glazedPane(b, { px: o.x, pz: o.plane, w: o.w, h: o.h,
                      y0: o.y - o.h / 2, cols: 4, rows: 2 });
      // deeper sill: reachable from the porch deck (0.75 m hop), so it is
      // the ground-floor perch
      surround(b, o, { sillExt: 0.12, sillD: 0.22 });
    },
    frontDoor:  () => frontDoor(b),
    garageDoor: () => garageDoor(b),
  };
  for (const o of H.OPENINGS) {
    const f = fill[o.id];
    if (!f) throw new Error(`hero/openings: nothing fills "${o.id}"`);
    f();
  }
  backDoor(b);              // not in OPENINGS: it is a hole in the BACK wall
}
